import { array, object, string } from "yup";
import { Category } from "protos/robot_syncer/plant_profile";
import {
  type CategoryOption,
  categoryRolesForCategories,
  getDefaultCategoryNames,
  hasStrictCategories,
  translateCategoryNames,
} from "portal/utils/categoryCollection/category";
import { Field, FormikConfig, useFormikContext } from "formik";
import { IconButton, Typography } from "@mui/material";
import { PlantProfileCategoryOptionsField } from "./PlantProfileCategoryOptionsField";
import { TextField } from "formik-mui";
import { TFunction } from "i18next";
import { UnsavedPlantProfile } from "portal/utils/categoryCollection/plantProfile";
import { usePlantProfileCategoryOptions } from "./usePlantProfileCategoryOptions";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import CopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/CloseOutlined";
import React, { FC, useMemo } from "react";

interface PlantProfileFormValue {
  name: string;
  categories: CategoryOption[];
}

interface SetPlantProfileFormProps {
  customerUuid?: string;
  nameOnly?: boolean;
}

export const SetPlantProfileForm: FC<SetPlantProfileFormProps> = ({
  customerUuid,
  nameOnly = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<PlantProfileFormValue>();

  const {
    categoryOptions,
    isLoading: areOptionsLoading,
    loadMore: loadMoreOptions,
    onSearchInputChange: onCategorySearchInputChange,
  } = usePlantProfileCategoryOptions(customerUuid);

  return (
    <div className="flex flex-col gap-4 pt-2">
      <Field
        component={TextField}
        name="name"
        label={t("models.categoryCollectionProfiles.fields.name")}
      />
      {!nameOnly && (
        <>
          <PlantProfileCategoryOptionsField
            name="categories"
            label={t("models.categoryCollectionProfiles.category_other_title")}
            categoryOptions={categoryOptions}
            loading={areOptionsLoading}
            onLoadMore={loadMoreOptions}
            onSearchInputChange={onCategorySearchInputChange}
            multiple
            freeSolo
          />
          <div className="flex flex-col">
            {values.categories.map((category) => {
              const chipText =
                category.chipIds.length > 0
                  ? ` (${category.chipIds.length} ${t(
                      "models.images.image_title",
                      {
                        count: category.chipIds.length,
                      }
                    )})`
                  : "";

              return (
                <div key={category.id} className="flex items-center">
                  <Typography component="p" variant="body2" className="m-0">
                    <span>{category.displayName}</span>
                    {!category.collection && chipText && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {chipText}
                      </Typography>
                    )}
                    {category.collection && (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          className="pl-2"
                        >
                          <CopyIcon className="text-sm" />{" "}
                          {category.collection.name}
                        </Typography>
                        {chipText && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {chipText}
                          </Typography>
                        )}
                      </>
                    )}
                  </Typography>
                  <IconButton
                    className="text-white"
                    size="small"
                    onClick={() => {
                      setFieldValue(
                        "categories",
                        values.categories.filter(
                          (value) => value.id !== category.id
                        )
                      );
                      setFieldTouched("categories", true);
                    }}
                  >
                    <DeleteIcon className="text-xs" />
                  </IconButton>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

type PlantProfileFormConfigOptions = {
  nameOnly?: boolean;
};

/**
 * Categories submitted from the fixed-profile form. A category copied from
 * another profile gets a new id and loses its almanac, so the profile stays
 * all-or-nothing. A category already on this profile keeps its id and almanac.
 */
export const categoriesFromFormValues = (
  categories: CategoryOption[],
  profileId: string | undefined
): Category[] =>
  categories.map(({ collection, versionId, displayName, ...category }) => {
    const copiedFromOtherProfile =
      collection !== undefined && collection.id !== profileId;
    if (!copiedFromOtherProfile) {
      return category;
    }
    const { almanac, ...withoutAlmanac } = category;
    return {
      ...withoutAlmanac,
      id: uuid(),
    };
  });

export const usePlantProfileFormConfig = (
  initialValue: UnsavedPlantProfile | undefined,
  onSubmit: (value: UnsavedPlantProfile) => void,
  t: TFunction,
  options?: PlantProfileFormConfigOptions
): FormikConfig<PlantProfileFormValue> => {
  const nameOnly = options?.nameOnly ?? false;
  const requiredCategories = getDefaultCategoryNames(t, false);
  const requiredCategoriesDisplay = getDefaultCategoryNames(t, true);

  const initialValues: PlantProfileFormValue = useMemo(() => {
    const cats = initialValue?.categories ?? [];
    const translate = translateCategoryNames(t, cats);
    return {
      name: initialValue?.name ?? "",
      categories: cats.map((c) => ({
        ...c,
        displayName: translate(c.name),
      })),
    };
  }, [initialValue, t]);

  const validationSchema = useMemo(() => {
    if (nameOnly) {
      return object({
        name: string().required(),
      });
    }
    return object({
      name: string().required(),
      categories: array()
        .of(
          object().shape({
            id: string().required(),
            name: string().required(),
            versionId: string().optional(),
            collection: object()
              .shape({ id: string(), name: string() })
              .optional(),
          })
        )
        .min(
          requiredCategories.length,
          `${t(
            "models.categoryCollectionProfiles.fields.categories.requiredBaseCategories"
          )}"${requiredCategoriesDisplay.join(", ")}"`
        )
        .max(
          requiredCategories.length,
          `${t(
            "models.categoryCollectionProfiles.fields.categories.requiredBaseCategories"
          )}"${requiredCategoriesDisplay.join(", ")}"`
        )
        .required()
        .test(
          "required-base-categories",
          `${t(
            "models.categoryCollectionProfiles.fields.categories.requiredBaseCategories"
          )}"${requiredCategoriesDisplay.join(", ")}"`,
          (values) => hasStrictCategories(values, requiredCategories)
        ),
    });
  }, [nameOnly, requiredCategories, requiredCategoriesDisplay, t]);

  return {
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: ({ name, categories }) => {
      if (nameOnly) {
        onSubmit({
          id: initialValue?.id ?? uuid(),
          name,
          categories: [],
          categoryRoles: {},
        });
        return;
      }

      const newCategories = categoriesFromFormValues(
        categories,
        initialValue?.id
      );

      const newUnsavedProfile: UnsavedPlantProfile = {
        id: initialValue?.id ?? uuid(),
        name,
        categories: newCategories,
        categoryRoles: categoryRolesForCategories(
          newCategories,
          initialValue?.categoryRoles
        ),
      };
      onSubmit(newUnsavedProfile);
    },
  };
};
